import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AUTH_API_BASE_URL, AUTH_SUPABASE_CLIENT } from './auth.tokens';
import { AuthStateService } from './auth-state.service';
import type {
	ConfigurationLoginResponse,
	OperatorLoginResponse,
	OperatorSessionResponse,
	OperatorSessionRestoreResponse,
} from './auth.models';

@Injectable({ providedIn: 'root' })
export class AuthApiService {
	private readonly http = inject(HttpClient);
	private readonly baseUrl = inject(AUTH_API_BASE_URL);
	private readonly supabase = inject(AUTH_SUPABASE_CLIENT);
	private readonly state = inject(AuthStateService);

	async configurationLogin(username: string, password: string): Promise<void> {
		const response = await firstValueFrom(
			this.http.post<ConfigurationLoginResponse>(
				`${this.baseUrl}/configuration-login`,
				{ username, password },
			),
		);
		const { error } = await this.supabase.auth.setSession({
			access_token: response.access_token,
			refresh_token: response.refresh_token,
		});
		if (error) throw error;
		this.state.setConfigurationUser(response.user);
	}

	async operatorLogin(pin: string): Promise<OperatorLoginResponse> {
		const response = await firstValueFrom(
			this.http.post<OperatorLoginResponse>(`${this.baseUrl}/operator-login`, {
				pin,
			}),
		);
		if ('session_token' in response)
			this.state.setOperatorSession(response.session_token, response.session);
		return response;
	}

	async selectStore(loginChallenge: string, storeId: string): Promise<void> {
		const response = await firstValueFrom(
			this.http.post<OperatorSessionResponse>(
				`${this.baseUrl}/operator-login`,
				{
					action: 'select-store',
					login_challenge: loginChallenge,
					store_id: storeId,
				},
			),
		);
		this.state.setOperatorSession(response.session_token, response.session);
	}

	async restoreOperatorSession(): Promise<boolean> {
		if (!this.state.operatorToken()) return false;
		try {
			const response = await firstValueFrom(
				this.http.post<OperatorSessionRestoreResponse>(
					`${this.baseUrl}/operator-login`,
					{ action: 'session' },
					{ headers: this.operatorHeaders() },
				),
			);
			this.state.setOperatorSession(
				this.state.operatorToken()!,
				response.session,
			);
			return true;
		} catch {
			this.state.clearOperator();
			return false;
		}
	}

	async restoreConfigurationSession(): Promise<boolean> {
		try {
			const { data } = await this.supabase.auth.getSession();
			const token = data.session?.access_token;
			if (!token) return false;
			const response = await firstValueFrom(
				this.http.post<{ user: ConfigurationLoginResponse['user'] }>(
					`${this.baseUrl}/configuration-admin`,
					{ action: 'context' },
					{ headers: new HttpHeaders({ Authorization: `Bearer ${token}` }) },
				),
			);
			this.state.setConfigurationUser(response.user);
			return true;
		} catch {
			this.state.clearConfiguration();
			return false;
		}
	}

	async switchStore(storeId: string): Promise<void> {
		const response = await firstValueFrom(
			this.http.post<OperatorSessionResponse>(
				`${this.baseUrl}/operator-login`,
				{
					action: 'switch-store',
					store_id: storeId,
				},
				{ headers: this.operatorHeaders() },
			),
		);
		this.state.setOperatorSession(response.session_token, response.session);
	}

	async logoutOperator(): Promise<void> {
		const token = this.state.operatorToken();
		try {
			if (token) {
				await firstValueFrom(
					this.http.post(
						`${this.baseUrl}/operator-login`,
						{ action: 'logout' },
						{ headers: this.operatorHeaders() },
					),
				);
			}
		} finally {
			this.state.clearOperator();
		}
	}

	async logoutConfiguration(): Promise<void> {
		await this.supabase.auth.signOut();
		this.state.clearConfiguration();
	}

	private operatorHeaders(): HttpHeaders {
		const token = this.state.operatorToken();
		return token
			? new HttpHeaders({ Authorization: `Bearer ${token}` })
			: new HttpHeaders();
	}
}
